import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { PeriodBreakdownItem, WeekBreakdownItem } from '../../models/stats.models';
import { formatDurationHHmm, ZoneConfig, ZONE_CONFIGS } from '../../helpers/general.helpers';
import { formatSeasonDurationHours, safeZoneArray } from '../../pages/training-log/training-log.helpers';
import { getSportLayerColor } from '../../pages/training-log/training-log.helpers';

dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(timezone);

const ISO_DATE_FRAGMENT = /\d{4}-\d{2}-\d{2}(?:T[\d:.]+(?:Z|[+-]\d{2}:?\d{2})?)?/g;

/** Parse an API ISO datetime into the user's local timezone. */
export const parseApiDateToLocal = (value: string): Dayjs => {
  if (!value?.trim()) {
    return dayjs('');
  }

  const trimmed = value.trim();
  if (trimmed.includes('T')) {
    return dayjs.utc(trimmed).tz(dayjs.tz.guess());
  }

  return dayjs(trimmed);
};

export const formatApiDateDisplay = (value: string, pattern = 'MMM D'): string => {
  const parsed = parseApiDateToLocal(value);
  return parsed.isValid() ? parsed.format(pattern) : value;
};

export const formatPeriodDateRangeLabel = (periodLabel: string): string => {
  const isoMatches = periodLabel.match(ISO_DATE_FRAGMENT);
  if (isoMatches && isoMatches.length >= 2) {
    return `${formatApiDateDisplay(isoMatches[0])} – ${formatApiDateDisplay(isoMatches[1])}`;
  }
  if (isoMatches?.length === 1) {
    return formatApiDateDisplay(isoMatches[0]);
  }
  return periodLabel;
};

export type PeriodStatus = 'current' | 'upcoming' | 'completed';

export interface BarSegment {
  flex: number;
  color: string;
}

export interface VsPrevBar {
  label: string;
  fillWidth: number;
  color: string;
}

export interface ZoneStat {
  label: string;
  durationSeconds: number;
  durationFormatted: string;
  distance: number;
  percent: number;
  config: ZoneConfig;
}

export interface SportStat {
  key: string;
  label: string;
  durationSeconds: number;
  durationFormatted: string;
  distance: number;
  percent: number;
  color: string;
}

export const buildBarSegments = (values: number[], colors: string[]): BarSegment[] => {
  const safe = values.map((v) => Math.max(0, v ?? 0));
  const total = safe.reduce((sum, v) => sum + v, 0);
  if (total === 0) {
    return [];
  }
  return safe.map((value, index) => ({
    flex: value,
    color: colors[index] ?? '#9CA3AF',
  }));
};

export const getVsPrevBar = (percentDiff: number): VsPrevBar => {
  const vsPrevPercent = 100 + (percentDiff ?? 0);
  const fillWidth = Math.min(100, Math.max(0, vsPrevPercent));
  const color = vsPrevPercent >= 100 ? '#2563EB' : vsPrevPercent >= 80 ? '#3B82F6' : '#93C5FD';

  return {
    label: `${Math.round(vsPrevPercent)}%`,
    fillWidth,
    color,
  };
};

export const formatVsPrevKpiLabel = (periodIndex: number, percentDiff: number, hourDeltaSeconds: number): string => {
  const sign = hourDeltaSeconds >= 0 ? '+' : '-';
  const hours = formatSeasonDurationHours(Math.abs(hourDeltaSeconds));
  const delta = `${sign}${hours}`;

  if (periodIndex === 1) {
    return `Season start · ${delta}`;
  }

  const pctSign = percentDiff >= 0 ? '+' : '';
  return `vs. P${periodIndex - 1} · ${pctSign}${Math.round(percentDiff)}%`;
};

export const formatVsPrevKpiValue = (percentDiff: number): string => {
  const sign = percentDiff >= 0 ? '+' : '';
  return `${sign}${Math.round(percentDiff)}%`;
};

export const getHourDelta = (currentSeconds: number, previousSeconds: number): number => {
  return (currentSeconds ?? 0) - (previousSeconds ?? 0);
};

export const groupWeeksByPeriod = (weeks: WeekBreakdownItem[], periodIndex: number): WeekBreakdownItem[] => {
  return weeks.filter((week) => week.period === periodIndex).sort((a, b) => a.index - b.index);
};

export const getPeriodDateRange = (
  period: PeriodBreakdownItem,
  weeks: WeekBreakdownItem[]
): { start: Dayjs | null; end: Dayjs | null } => {
  const periodWeeks = groupWeeksByPeriod(weeks, period.index);
  if (periodWeeks.length > 0) {
    const dates = periodWeeks.map((w) => parseApiDateToLocal(w.week));
    return {
      start: dates.reduce((min, d) => (d.isBefore(min) ? d : min), dates[0]),
      end: dates.reduce((max, d) => (d.isAfter(max) ? d : max), dates[0]).add(6, 'day'),
    };
  }

  return parsePeriodDateRange(period.period);
};

export const parsePeriodDateRange = (periodLabel: string): { start: Dayjs | null; end: Dayjs | null } => {
  const isoMatches = periodLabel.match(ISO_DATE_FRAGMENT);
  if (isoMatches && isoMatches.length >= 2) {
    const start = parseApiDateToLocal(isoMatches[0]);
    const end = parseApiDateToLocal(isoMatches[1]);
    return {
      start: start.isValid() ? start : null,
      end: end.isValid() ? end : null,
    };
  }
  if (isoMatches?.length === 1) {
    const start = parseApiDateToLocal(isoMatches[0]);
    return { start: start.isValid() ? start : null, end: null };
  }

  const parts = periodLabel.split(/[–-]/).map((p) => p.trim());
  if (parts.length < 2) {
    return { start: null, end: null };
  }

  const currentYear = dayjs().year();
  let start = dayjs(`${parts[0]} ${currentYear}`, 'MMM D YYYY', true);
  let end = dayjs(`${parts[1]} ${currentYear}`, 'MMM D YYYY', true);

  if (!start.isValid()) {
    start = parseApiDateToLocal(parts[0]);
  }
  if (!end.isValid()) {
    end = parseApiDateToLocal(parts[1]);
  }

  if (end.isBefore(start)) {
    end = end.add(1, 'year');
  }

  return { start: start.isValid() ? start : null, end: end.isValid() ? end : null };
};

export const getPeriodStatus = (
  period: PeriodBreakdownItem,
  weeks: WeekBreakdownItem[],
  referenceDate: Dayjs = dayjs()
): PeriodStatus => {
  const hasVolume = (period.duration?.total ?? 0) > 0;
  const { start, end } = getPeriodDateRange(period, weeks);

  if (start && end && referenceDate.isBetween(start, end, 'day', '[]')) {
    return 'current';
  }

  if (!hasVolume && start && start.isAfter(referenceDate, 'day')) {
    return 'upcoming';
  }

  if (!hasVolume && start && !start.isAfter(referenceDate, 'day')) {
    return 'upcoming';
  }

  if (hasVolume && end && end.isBefore(referenceDate, 'day')) {
    return 'completed';
  }

  if (hasVolume) {
    return 'current';
  }

  return 'upcoming';
};

export const isCurrentWeek = (weekDate: string, referenceDate: Dayjs = dayjs()): boolean => {
  const start = parseApiDateToLocal(weekDate).startOf('day');
  const end = start.add(6, 'day').endOf('day');
  return referenceDate.isBetween(start, end, 'day', '[]');
};

export const buildZoneStats = (durationBreakdown: number[], distanceBreakdown: number[]): ZoneStat[] => {
  const durations = safeZoneArray(durationBreakdown);
  const distances = safeZoneArray(distanceBreakdown);
  const totalDuration = durations.reduce((sum, v) => sum + v, 0);

  return ZONE_CONFIGS.map((config, index) => {
    const durationSeconds = durations[index];
    const distance = distances[index];
    const percent = totalDuration > 0 ? (durationSeconds / totalDuration) * 100 : 0;

    return {
      label: config.label,
      durationSeconds,
      durationFormatted: durationSeconds > 0 ? `${formatDurationHHmm(durationSeconds)}h` : '0:00h',
      distance,
      percent,
      config,
    };
  });
};

export const buildSportStats = (sport: PeriodBreakdownItem['sport']): SportStat[] => {
  const entries = Object.entries(sport ?? {});
  const totalDuration = entries.reduce((sum, [, metrics]) => sum + (metrics?.duration ?? 0), 0);

  return entries
    .map(([key, metrics], index) => {
      const durationSeconds = metrics?.duration ?? 0;
      const distance = metrics?.distance ?? 0;
      const percent = totalDuration > 0 ? (durationSeconds / totalDuration) * 100 : 0;
      return {
        key,
        label: key,
        durationSeconds,
        durationFormatted: formatSeasonDurationHours(durationSeconds),
        distance,
        percent,
        color: getSportLayerColor(key, index),
      };
    })
    .sort((a, b) => b.durationSeconds - a.durationSeconds);
};

export const formatZoneDurationCell = (seconds: number): string => {
  if (!seconds) {
    return '0:00';
  }
  return formatDurationHHmm(seconds);
};

export const formatZoneDistanceCell = (distance: number): string => {
  if (!distance) {
    return '0.0';
  }
  return distance % 1 === 0 ? String(distance) : distance.toFixed(1);
};

export const getUpcomingPeriodStartLabel = (period: PeriodBreakdownItem): string => {
  const { start } = parsePeriodDateRange(period.period);
  if (start?.isValid()) {
    return start.format('MMM D');
  }
  const isoMatches = period.period.match(ISO_DATE_FRAGMENT);
  if (isoMatches?.[0]) {
    return formatApiDateDisplay(isoMatches[0]);
  }
  return period.period.split(/[–-]/)[0]?.trim() ?? '';
};

export const getPeriodTitle = (period: PeriodBreakdownItem, status: PeriodStatus): string => {
  if (status === 'upcoming') {
    return `Period ${period.index} · Upcoming`;
  }
  return `Period ${period.index}`;
};
